import type { Row, SqlValue, StorageAdapter } from "./adapter.js";

/** A point-in-time copy of every table, keyed by table name. JSON-serializable. */
export type Snapshot = Record<string, Row[]>;

/** What changed in one table since the last successful write. */
export interface TableDelta {
  /** Rows inserted or updated, by primary key (`id`, or `key` for settings). */
  upserts: Row[];
  /** Primary keys removed. */
  deletes: string[];
  /** The whole table was truncated — apply before `upserts`. */
  cleared: boolean;
}

/** Everything a persistence layer needs to write an incremental update. */
export interface PersistenceDelta {
  /** Every table that currently exists, so an empty one still round-trips. */
  tables: string[];
  changes: Map<string, TableDelta>;
}

/** Optional durable backing for {@link MemoryAdapter} (e.g. IndexedDB in the browser). */
export interface MemoryPersistence {
  load(): Promise<Snapshot | null>;
  save(snap: Snapshot): Promise<void>;
  /**
   * Incremental write. Preferred over {@link save} when implemented: writing the
   * whole snapshot means structured-cloning every row on the main thread, which
   * on a real dataset is ~55 ms after EVERY edit — and ~45 ms of that is the
   * tasks table alone, so a per-table split would barely help. A per-row write
   * costs what the edit actually touched.
   */
  saveDelta?(snap: Snapshot, delta: PersistenceDelta): Promise<void>;
}

/** Primary key of a row: `id` everywhere except `settings`, which is keyed by
 *  `key`. Null when a row has neither — the caller then rewrites the table. */
function rowKey(row: Row): string | null {
  const id = row["id"] ?? row["key"];
  return typeof id === "string" ? id : null;
}

/** Pending changes for one table, held key-indexed so recording a change is
 *  O(1). Materialized into a {@link TableDelta} at flush time — the public
 *  shape a persistence layer sees is unchanged. */
interface PendingDelta {
  upserts: Map<string, Row>;
  deletes: Set<string>;
  cleared: boolean;
}

/**
 * Trivial in-memory store used by tests and the web dev server before sql.js is wired.
 * It does NOT execute SQL — instead it interprets a small set of pseudo-SQL verbs.
 * Real adapters (tauri-sqlite, capacitor-sqlite, sqljs) execute real SQL.
 *
 * When constructed with a {@link MemoryPersistence}, it seeds from it on `open()`
 * and debounce-saves a snapshot after every mutation, so browser data survives reload.
 */
export class MemoryAdapter implements StorageAdapter {
  private tables = new Map<string, Row[]>();
  /**
   * Primary-key index per table, built lazily and maintained on every write.
   *
   * Without it every write verb was a table scan, so a bulk import was
   * QUADRATIC: restoring the reference backup ran ~19.9k `INSERT … ON CONFLICT`
   * statements against a 12.5k-row table, and the conflict lookup alone is
   * 78M comparisons. Measured, that pass cost **~2.15 s** — twice, because a
   * "replace" tombstones every row before re-inserting it.
   */
  private indexes = new Map<string, Map<string, Row>>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pending row-level changes since the last successful write, per table. */
  private pending = new Map<string, PendingDelta>();
  /** Set when a table's rows carry no primary key, or the table list changed —
   *  the next write falls back to a full snapshot save. */
  private needsFullSave = false;

  constructor(private readonly persistence?: MemoryPersistence) {}

  async open(): Promise<void> {
    if (!this.persistence) return;
    const snap = await this.persistence.load();
    if (snap) for (const [table, rows] of Object.entries(snap)) this.tables.set(table, rows);
    this.indexes.clear();
  }

  async close(): Promise<void> {
    await this.flush();
  }

  async execute(sql: string, params: ReadonlyArray<SqlValue> = []): Promise<{ rowsAffected: number }> {
    const op = parseOp(sql);
    if (op.kind === "create") {
      if (!this.tables.has(op.table)) {
        this.tables.set(op.table, []);
        // The table list is persisted metadata, not rows — take the slow path
        // once so an empty new table round-trips.
        this.needsFullSave = true;
      }
      this.scheduleSave();
      return { rowsAffected: 0 };
    }
    if (op.kind === "insert") {
      const rows = this.tables.get(op.table) ?? [];
      const row: Row = {};
      op.cols.forEach((c, i) => { row[c] = params[i] ?? null; });
      // Honor `INSERT ... ON CONFLICT(<col>) DO UPDATE`: apply the DO UPDATE SET
      // clause (like real SQLite) instead of appending a duplicate. That clause
      // matters — the task upsert deliberately leaves the remote-link columns
      // alone on conflict, and the `mut=mut+1` chokepoint expressions live there.
      if (op.conflict) {
        const existing = this.findByColumn(op.table, rows, op.conflict, row[op.conflict] ?? null);
        if (existing) {
          const before = rowKey(existing);
          // Every upsert this app issues carries a DO UPDATE SET clause. If one
          // ever fails to parse, FAIL rather than falling back to "overwrite
          // every column": that fallback would clobber the remote-link columns
          // the task upsert deliberately leaves alone on conflict, silently
          // unlinking rows on the platform most users are on.
          if (!op.conflictSets) {
            throw new Error(`MemoryAdapter: unparsed ON CONFLICT DO UPDATE clause on ${op.table}`);
          }
          applySetItems(existing, op.conflictSets, params, op.cols.length, row);
          this.tables.set(op.table, rows);
          this.reindex(op.table, existing, before);
          this.markUpsert(op.table, existing);
          this.scheduleSave();
          return { rowsAffected: 1 };
        }
      }
      rows.push(row);
      this.tables.set(op.table, rows);
      this.reindex(op.table, row, null);
      this.markUpsert(op.table, row);
      this.scheduleSave();
      return { rowsAffected: 1 };
    }
    if (op.kind === "update") {
      const rows = this.tables.get(op.table) ?? [];
      const id = params[params.length - 1];
      const indexed = typeof id === "string" ? this.indexFor(op.table, rows).get(id) : undefined;
      // The index is keyed by `id` (or `key`), which is exactly what an
      // `UPDATE … WHERE id=?` matches, so the scan is only needed when the row
      // carries no primary key at all.
      const matches = indexed && indexed["id"] === id ? [indexed] : rows.filter((r) => r["id"] === id);
      for (const r of matches) {
        const before = rowKey(r);
        applySetItems(r, op.sets, params, 0, null);
        this.reindex(op.table, r, before);
        this.markUpsert(op.table, r);
      }
      this.scheduleSave();
      return { rowsAffected: matches.length };
    }
    if (op.kind === "delete") {
      const rows = this.tables.get(op.table) ?? [];
      const all = !(op.where === "eq" && op.eqCol);
      if (all) {
        this.tables.set(op.table, []);
        this.indexes.delete(op.table);
        this.markCleared(op.table);
        this.scheduleSave();
        return { rowsAffected: rows.length };
      }
      const kept: Row[] = [];
      const removed: Row[] = [];
      for (const r of rows) (r[op.eqCol!] !== params[0] ? kept : removed).push(r);
      this.tables.set(op.table, kept);
      const index = this.indexes.get(op.table);
      for (const r of removed) {
        const key = rowKey(r);
        if (key !== null) index?.delete(key);
        this.markDelete(op.table, r);
      }
      this.scheduleSave();
      return { rowsAffected: removed.length };
    }
    return { rowsAffected: 0 };
  }

  /** Primary-key index for a table, built on first use. */
  private indexFor(table: string, rows: Row[]): Map<string, Row> {
    let index = this.indexes.get(table);
    if (!index) {
      index = new Map();
      for (const r of rows) {
        const key = rowKey(r);
        if (key !== null) index.set(key, r);
      }
      this.indexes.set(table, index);
    }
    return index;
  }

  /** `WHERE <col>=?` lookup — indexed when the column IS the primary key
   *  (`id`, or `key` for settings), a scan otherwise. */
  private findByColumn(table: string, rows: Row[], col: string, value: SqlValue): Row | undefined {
    if ((col === "id" || col === "key") && typeof value === "string") {
      const hit = this.indexFor(table, rows).get(value);
      return hit && hit[col] === value ? hit : undefined;
    }
    return rows.find((r) => r[col] === value);
  }

  /** Keep the index in step with a row whose key may have changed. */
  private reindex(table: string, row: Row, previousKey: string | null): void {
    const index = this.indexes.get(table);
    if (!index) return;
    const key = rowKey(row);
    if (previousKey !== null && previousKey !== key) index.delete(previousKey);
    if (key !== null) index.set(key, row);
  }

  async select<T = Row>(sql: string, params: ReadonlyArray<SqlValue> = []): Promise<T[]> {
    const op = parseOp(sql);
    if (op.kind !== "select") return [];
    const rows = this.tables.get(op.table) ?? [];
    const out = op.where === "eq" && op.eqCol
      ? rows.filter((r) => r[op.eqCol!] === params[0])
      : op.where === "active"
      ? rows.filter((r) => !r["deleted_at"])
      : rows;
    return out as unknown as T[];
  }

  private snapshot(): Snapshot {
    return Object.fromEntries(this.tables);
  }

  private deltaFor(table: string): PendingDelta {
    let entry = this.pending.get(table);
    if (!entry) this.pending.set(table, (entry = { upserts: new Map(), deletes: new Set(), cleared: false }));
    return entry;
  }

  private markUpsert(table: string, row: Row): void {
    const key = rowKey(row);
    // A row with no primary key cannot be addressed individually — fall back to
    // rewriting everything rather than silently dropping the change.
    if (key === null) { this.needsFullSave = true; return; }
    const entry = this.deltaFor(table);
    // The row object is mutated in place by later updates; keying by primary key
    // means one entry per row no matter how many times it is touched.
    entry.upserts.set(key, row);
    entry.deletes.delete(key);
  }

  private markDelete(table: string, row: Row): void {
    const key = rowKey(row);
    if (key === null) { this.needsFullSave = true; return; }
    const entry = this.deltaFor(table);
    entry.upserts.delete(key);
    entry.deletes.add(key);
  }

  private markCleared(table: string): void {
    this.pending.set(table, { upserts: new Map(), deletes: new Set(), cleared: true });
  }

  private scheduleSave(): void {
    if (!this.persistence) return;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => { void this.flush(); }, 150);
  }

  /** Persist immediately, cancelling the pending debounce. Public so the app can
   *  flush on pagehide/visibility-hidden — otherwise an edit made within the
   *  debounce window of closing the tab is lost. Safe to call repeatedly.
   *
   *  Writes only what changed when the persistence supports it. The pending
   *  delta is cleared only after the write RESOLVES, so a failed write leaves
   *  the changes queued for the next flush instead of losing them. */
  async flush(): Promise<void> {
    if (!this.persistence) return;
    if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
    const snapshot = this.snapshot();
    if (!this.persistence.saveDelta || this.needsFullSave) {
      await this.persistence.save(snapshot);
      this.pending.clear();
      this.needsFullSave = false;
      return;
    }
    if (this.pending.size === 0) return;
    const drained = this.pending;
    this.pending = new Map();
    const changes = new Map<string, TableDelta>();
    for (const [table, entry] of drained) {
      changes.set(table, { upserts: [...entry.upserts.values()], deletes: [...entry.deletes], cleared: entry.cleared });
    }
    const delta: PersistenceDelta = { tables: [...this.tables.keys()], changes };
    try {
      await this.persistence.saveDelta(snapshot, delta);
    } catch (e) {
      // Re-queue: the next flush must still carry these rows.
      for (const [table, entry] of drained) {
        const current = this.pending.get(table);
        if (!current) this.pending.set(table, entry);
        else {
          current.cleared ||= entry.cleared;
          for (const [key, row] of entry.upserts) if (!current.upserts.has(key)) current.upserts.set(key, row);
          for (const key of entry.deletes) current.deletes.add(key);
        }
      }
      throw e;
    }
  }
}

type Op =
  | { kind: "create"; table: string }
  | { kind: "insert"; table: string; cols: string[]; conflict?: string | undefined; conflictSets?: SetItem[] | undefined }
  | { kind: "update"; table: string; sets: SetItem[] }
  | { kind: "delete"; table: string; where: "eq" | "all"; eqCol?: string | undefined }
  | { kind: "select"; table: string; where: "eq" | "active" | "all"; eqCol?: string | undefined };

/** One `col=<rhs>` assignment in a SET clause. Besides the plain `col=?`
 *  placeholder, the repositories use two SQLite expression forms that this
 *  adapter must evaluate the way SQLite does — against the row's PRE-UPDATE
 *  values: `mut=mut+1` (the write chokepoint's atomic bump) and
 *  `server_acked_mut=mut+1` (a self-acknowledging write; same OLD `mut`).
 *  `excluded.<col>` (upserts) takes the value the INSERT carried. */
type SetItem =
  | { col: string; src: "param" }
  | { col: string; src: "excluded"; from: string }
  | { col: string; src: "add"; base: string; add: number };

function parseSetItems(text: string): SetItem[] {
  const items: SetItem[] = [];
  for (const part of text.split(",")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const col = part.slice(0, eq).trim();
    const rhs = part.slice(eq + 1).trim();
    if (!col) continue;
    const excluded = rhs.match(/^excluded\.([a-z_][a-z0-9_]*)$/);
    const add = rhs.match(/^([a-z_][a-z0-9_]*)\s*\+\s*(\d+)$/);
    if (excluded) items.push({ col, src: "excluded", from: excluded[1]! });
    else if (add) items.push({ col, src: "add", base: add[1]!, add: Number(add[2]) });
    else items.push({ col, src: "param" });
  }
  return items;
}

/** Apply a SET clause to `row`. All expressions read the row's PRE-UPDATE values
 *  (SQLite semantics), so `mut=mut+1, acked=mut+1` lands both at old-mut+1.
 *  `paramOffset` skips the params an INSERT's VALUES list already consumed;
 *  `excludedRow` is that INSERT's proposed row (null outside upserts). */
function applySetItems(
  row: Row,
  sets: SetItem[],
  params: ReadonlyArray<SqlValue>,
  paramOffset: number,
  excludedRow: Row | null,
): void {
  const old: Row = { ...row };
  let p = paramOffset;
  for (const it of sets) {
    if (it.src === "param") row[it.col] = params[p++] ?? null;
    else if (it.src === "excluded") row[it.col] = excludedRow?.[it.from] ?? null;
    else row[it.col] = Number(old[it.base] ?? 0) + it.add;
  }
}

/** Parsed statements, keyed by the SQL text. Repositories build their SQL from
 *  static column lists, so the set is small and fixed — but a bulk import runs
 *  the SAME ~900-character upsert once per row, and lower-casing plus five
 *  regexes per row is not free. Bounded so an interpolated statement cannot
 *  grow it without limit. */
const opCache = new Map<string, Op>();
const OP_CACHE_MAX = 200;

function parseOp(sql: string): Op {
  const cached = opCache.get(sql);
  if (cached) return cached;
  const op = parseOpUncached(sql);
  if (opCache.size >= OP_CACHE_MAX) opCache.clear();
  opCache.set(sql, op);
  return op;
}

function parseOpUncached(sql: string): Op {
  const s = sql.trim().toLowerCase();
  const tableMatch = s.match(/(?:from|into|update|table)(?:\s+if\s+not\s+exists)?\s+([a-z_][a-z0-9_]*)/);
  const table = tableMatch?.[1] ?? "unknown";
  if (s.startsWith("create")) return { kind: "create", table };
  if (s.startsWith("insert")) {
    const cols = (s.match(/\(([^)]+)\)\s*values/)?.[1] ?? "")
      .split(",").map((c) => c.trim()).filter(Boolean);
    const conflict = s.match(/on\s+conflict\s*\(\s*([a-z_]+)\s*\)/)?.[1];
    const conflictSetText = s.match(/on\s+conflict\s*\(\s*[a-z_]+\s*\)\s*do\s+update\s+set\s+([\s\S]+)$/)?.[1];
    const conflictSets = conflictSetText ? parseSetItems(conflictSetText) : undefined;
    return { kind: "insert", table, cols, conflict, conflictSets };
  }
  if (s.startsWith("update")) {
    // `[\s\S]` (not `.`) so a MULTILINE `SET a=?,\n b=? WHERE id=?` clause is
    // captured whole — otherwise the SET column list is parsed as empty and the
    // UPDATE silently writes no columns.
    const sets = parseSetItems(s.match(/set\s+([\s\S]+?)\s+where/)?.[1] ?? "");
    return { kind: "update", table, sets };
  }
  // Generalized equality filter: `WHERE <col>=?` (covers id, key, …). The
  // `deleted_at IS NULL` "active" filter takes precedence when present.
  const eqCol = s.match(/where\s+([a-z_]+)\s*=\s*\?/)?.[1];
  if (s.startsWith("delete")) return { kind: "delete", table, where: eqCol ? "eq" : "all", eqCol };
  const where = /deleted_at\s+is\s+null/.test(s) ? "active" : eqCol ? "eq" : "all";
  return { kind: "select", table, where, eqCol };
}
